import { useEffect, useState } from 'react';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAnalysisProfileQuery } from '../api/useAnalysisProfileQuery';
import { useAnalysisResultsQuery } from '../api/useAnalysisResultsQuery';

/**
 * True from the moment the profile leaves PROCESSING until the results refetch
 * that follows it lands, when the cached results still describe the run before.
 * The profile poll reports the new status first, so until the results response
 * arrives those results can be the previous run's (its stale flag, its cells).
 *
 * Results polled while the run was PROCESSING already describe the current run,
 * so once any of them has landed the results are not behind, and the table that
 * filled in during the run stays on screen through the final refetch.
 *
 * Tracks the results' `dataUpdatedAt` at the start of the run instead of comparing
 * statuses: after a re-process the cached results carry the previous run's
 * terminal status, which already equals the profile's new one.
 */
export const useResultsBehindProfile = (): boolean => {
  const { projectId, analysisProfileId } = useAnalysisRouteParams();
  const resultsQuery = useAnalysisResultsQuery({ projectId, analysisProfileId });
  const { data: profileEnvelope } = useAnalysisProfileQuery({ projectId, analysisProfileId });

  const profileStatus = profileEnvelope?.data.latestStatus;
  const isProcessing = profileStatus === 'PROCESSING';
  const resultsUpdatedAt = resultsQuery.dataUpdatedAt;

  // `dataUpdatedAt` of the results cached when the run started; they are the
  // previous run's until a newer response replaces them. Null outside a run.
  const [updatedAtAtRunStart, setUpdatedAtAtRunStart] = useState<number | null>(null);
  useEffect(() => {
    if (isProcessing && updatedAtAtRunStart === null) {
      setUpdatedAtAtRunStart(resultsUpdatedAt);
    } else if (
      !isProcessing &&
      updatedAtAtRunStart !== null &&
      resultsUpdatedAt !== updatedAtAtRunStart
    ) {
      // The run is over and newer results have landed: nothing is behind.
      setUpdatedAtAtRunStart(null);
    }
  }, [isProcessing, resultsUpdatedAt, updatedAtAtRunStart]);

  // `isFetching` bounds the flag if the refetch errors (data never advances), so
  // a failed refetch doesn't hold the loading state forever.
  return (
    !isProcessing &&
    updatedAtAtRunStart !== null &&
    resultsUpdatedAt === updatedAtAtRunStart &&
    resultsQuery.isFetching
  );
};
